
import clsx from "clsx"
import dayjs from "dayjs"
import duration from 'dayjs/plugin/duration'
import { motion } from "framer-motion"
import { useCallback, useEffect, useState } from "react"

const DEFAULT_START_AT = '2025-08-03T21:00:00.000+08:00'  // 默认北京时间 2025.8.3 21:00

dayjs.extend(duration)

const getRemainTime = (endTime) => dayjs.duration(dayjs(endTime).diff(dayjs()))
const getCurrentTime = () => dayjs().format('HH:mm:ss')
const getCurrentYear = () => dayjs().format('YYYY')

// 解析URL参数获取开始时间
const getStartTimeFromQuery = () => {
  if (typeof window === 'undefined') return null
  
  const urlParams = new URLSearchParams(window.location.search)
  const startAt = urlParams.get('startAt')
  const start = urlParams.get('start')
  const finalStartAt = startAt || start
  
  if (!finalStartAt) return null
  
  try {
    // 用户友好的格式，全部当作北京时间：
    // 1. 2025-07-22T14:50:00
    // 2. 2025-07-22 14:50
    // 3. 2025-07-22T14:50
    
    let normalizedTime = finalStartAt.trim()
    
    // 移除任何用户可能输入的时区信息，强制使用北京时间
    normalizedTime = normalizedTime
      .replace(/\+\d{2}:\d{2}$/, '')  // 移除 +08:00 格式
      .replace(/Z$/, '')              // 移除 Z (UTC)
      .replace(/ \d{2}:\d{2}$/, '')   // 移除被URL解码的 08:00
    
    // 标准化格式
    if (normalizedTime.includes(' ') && !normalizedTime.includes('T')) {
      normalizedTime = normalizedTime.replace(' ', 'T')
    }
    
    // 确保有秒数
    if (!normalizedTime.match(/:\d{2}:\d{2}/)) {
      normalizedTime += ':00'
    }
    
    // 强制添加北京时区
    normalizedTime += '+08:00'
    
    const parsedTime = dayjs(normalizedTime)
    return parsedTime.isValid() ? parsedTime.format() : null
  } catch {
    return null
  }
}

export default function Page() {
  const [stage, setStage] = useState(0)  // 0: 实时时间, 1: 第一个倒计时, 2: 第二个倒计时
  const [timeRemaining, setTimeRemaining] = useState(dayjs.duration(0))
  const [currentTime, setCurrentTime] = useState(getCurrentTime())
  const [startAt, setStartAt] = useState(DEFAULT_START_AT)  // 用state存储开始时间
  
  // 动态计算时间节点
  const SHOW_REAL_TIME_UNTIL = dayjs(startAt).subtract(10, 'minute').format()
  const END_AT = dayjs(startAt).add(24, 'hour').format()

  const CurrentTime = useCallback(() => {
    const [hours, minutes, seconds] = currentTime.split(':')
    return (
      <div className="flex items-center justify-center gap-2">
        <CountdownText content={hours} />
        <CountdownText content=':' isNumber={false} />
        <CountdownText content={minutes} />
        <CountdownText content=':' isNumber={false} />
        <CountdownText content={seconds} />
      </div>
    )
  }, [currentTime])

  const Countdown = useCallback(() => {
    if (timeRemaining.asSeconds() < 0) return (
      <div className="flex items-center justify-center gap-2">
        <CountdownText content='00' />
        <CountdownText content=':' isNumber={false} />
        <CountdownText content='00' />
        <CountdownText content=':' isNumber={false} />
        <CountdownText content='00' />
      </div>
    )

    const hours = formatWithPadding(timeRemaining.asHours())
    const minutes = formatWithPadding(timeRemaining.minutes())
    const seconds = formatWithPadding(timeRemaining.seconds())

    return (
      <div className="flex items-center justify-center gap-2">
        <CountdownText content={hours} />
        <CountdownText content=':' isNumber={false} />
        <CountdownText content={minutes} />
        <CountdownText content=':' isNumber={false} />
        <CountdownText content={seconds} />
      </div>
    )
  }, [timeRemaining])

  useEffect(() => {
    // 解析URL参数
    const queryStartAt = getStartTimeFromQuery()
    if (queryStartAt) {
      setStartAt(queryStartAt)
    }
  }, [])

  useEffect(() => {
    let timerId:ReturnType<typeof setTimeout>

    const updateState = () => {
      const now = dayjs();
      if (now.isBefore(SHOW_REAL_TIME_UNTIL)) {
        setStage(0);
        setCurrentTime(getCurrentTime());
      } else if (now.isBefore(startAt)) {
        setStage(1);
        setTimeRemaining(getRemainTime(startAt));
      } else {
        setStage(2);
        setTimeRemaining(getRemainTime(END_AT));
      }

      // 设置下一次更新
      timerId = setTimeout(updateState, 1000);
    };

    // 立即执行一次更新
    updateState();

    // 组件卸载时清理定时器
    return () => clearTimeout(timerId);
  }, [startAt, SHOW_REAL_TIME_UNTIL, END_AT]); // 依赖于startAt state

  return (
    <>
      <img
        className="pointer-events-none absolute top-0 left-0 w-full h-full object-cover object-[18%] sm:object-left-top"
        style={{ background: "linear-gradient(123deg, rgba(0, 0, 0, 0.20) 0%, rgba(0, 0, 0, 0) 100%); box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25)" }}
        // src="/capstone/bg-2024.png"
        src={`https://pub-8c398403e9854592b392ba5a694d1df9.r2.dev/bg-${getCurrentYear()}.png`}
        alt="bg"
      />
      <main className="absolute top-0 left-0 w-full h-full p-8 sm:p-14 text-white flex flex-col justify-between items-stretch">
        {stage === 0 ? (
          <motion.section
            id="CURRENT"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1.2 }}
            transition={{ duration: 1 }}
            className="fixed inset-0 grid place-content-center"
          >
            <CurrentTime />
          </motion.section>
        ) : (
          <section
            id="COUNTDOWN"
            className="duration-300 fixed inset-0 grid place-content-center"
          >
            <Countdown />
          </section>
        )}
      </main>
    </>
  )
}

function CountdownText({ content, isNumber = true }: { content: string, isNumber?: boolean }) {
  return (
    <p
      style={{
        WebkitTextStroke: isNumber ? '2px #FFF' : '3.75px #FFF',
        WebkitTextFillColor: '#00000050',
      }}
      className={clsx(isNumber ? 'scale-x-125' : 'mb-4 scale-75', "font-neox-mono text-[64px] sm:text-[128px] md:text-[160px] lg:text-[200px] xl:text-[256px] font-medium origin-center")}
    >
      {content
        .replace('0', 'O')
        .replace('0', 'O')
        .replace('0', 'O')
        .replace('0', 'O')
        .replace('0', 'O')
        .replace('0', 'O')
      }
    </p>
  )
}

function formatWithPadding(num: number) {
  const fixedNum = Math.floor(num).toString()
  return fixedNum.length >= 2 ? fixedNum : '0' + fixedNum
}